import * as React from "react";
import styled from "styled-components";
import Box from "@mui/material/Box";
import { Link as RouteLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, Grid, Stack } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import RangePickerModal from "src/components/range-picker-modal";
import { SprintsContextProvider } from "src/scenes/dashboard/selected-sprint-provider";
import { Helmet } from "react-helmet";
import DemoBarEnterprise from "src/components/demo-bar-enterprise";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
    EditModes,
    useTemplateViewEdit,
} from "src/components/providers/template-view-edit-provider";
import TemplateDashboardTitle from "src/components/template/template-dashboard-title";
import Typography from "@mui/material/Typography";
import { ActiveIntegrationsProvider } from "./active-integrations-provider";
import DemoBarPersonal from "../../components/demo-bar-personal";
import Portal from "../../components/portal";
import DemoBarDemo from "../../components/demo-bar-demo";

const NavButton = styled(Button).withConfig({
    shouldForwardProp: (prop) => prop !== "active",
})`
    & > h1 {
        font-size: 13px;
        margin: 0;
        padding: 0;
    }
    font-size: 13px;
    min-height: 32px;
    text-transform: none;
    cursor: pointer;
    margin: 12px;
    min-width: 126px;
    color: ${({ theme, active }) =>
        active ? theme.palette.primary.main : theme.palette.text.primary};
    border: 1px solid
        ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
    border-radius: 6px;

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        border: 1px solid
            ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
        background-color: transparent;
    }
`;

const DatepickerButton = styled(Button)`
    font-size: 12px;
    width: 257px;
    z-index: 10;
`;

const Dashboard = () => {
    const [openDatepicker, setOpenDatepicker] = React.useState(false);
    const { pathname } = useLocation();
    const pathnameSplit = pathname.split("/");
    const path = pathnameSplit[pathnameSplit.length - 1];

    const navigate = useNavigate();

    const toggleDatepicker = () => setOpenDatepicker(!openDatepicker);
    // const toggleSprintpicker = () => setOpenSprintpicker(!openSprintpicker);

    const redirectToTemplatesDetails = () => {
        return navigate("/templates-details");
    };

    const { onEditMode, view, editView } = useTemplateViewEdit();

    return (
        <ActiveIntegrationsProvider>
            <SprintsContextProvider>
                <Helmet>
                    <title>Adadot - Your Dashboard</title>
                </Helmet>
                <Portal>
                    <DemoBarPersonal />
                    <DemoBarDemo />
                    <DemoBarEnterprise />
                </Portal>
                <Box sx={{ width: "100%", typography: "body1" }}>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        className="tour-dashboard"
                    >
                        <Grid item md={12}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ width: "100%" }}
                            >
                                <Typography style={{ fontSize: "13px" }}>
                                    Personal dashboards
                                </Typography>
                                {path !== "insights" && (
                                    <DatepickerButton
                                        startIcon={<DateRangeIcon />}
                                        onClick={toggleDatepicker}
                                    >
                                        Select Dates / Sprint
                                    </DatepickerButton>
                                )}
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            sx={{ width: "auto" }}
                            md={9}
                        >
                            {!onEditMode && (
                                <NavButton
                                    variant="outlined"
                                    active={path === "insights"}
                                    component={RouteLink}
                                    to="/dashboard/insights"
                                >
                                    {path === "insights" ? <h1>Insights</h1> : <>Insights</>}
                                </NavButton>
                            )}
                            {view &&
                                view.dashboards.map((dashboard, dashboardIndex) => (
                                    <TemplateDashboardTitle
                                        dashboardIndex={dashboardIndex}
                                        dashboard={dashboard}
                                        key={dashboard.id}
                                    />
                                ))}
                            {onEditMode && view && (
                                <Button
                                    startIcon={<PlaylistAddCircleIcon />}
                                    variant="contained"
                                    size="small"
                                    style={{ borderRadius: "24px", marginLeft: "26px" }}
                                    onClick={() => {
                                        if (editView) {
                                            editView({
                                                dashboardIndex: view.dashboards.length,
                                                editMode: EditModes.addDashboard,
                                            });
                                        }
                                    }}
                                >
                                    ADD DASHBOARD
                                </Button>
                            )}
                        </Grid>
                        <Grid item md={3}>
                            <Grid container alignItems="center" justifyContent="flex-end">
                                {!onEditMode ? (
                                    <Grid item>
                                        <Stack
                                            direction="column"
                                            alignItems="center"
                                            justifyContent="flex-end"
                                            marginRight={1}
                                        >
                                            <Stack
                                                direction="row"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                <Button
                                                    startIcon={<ViewListIcon />}
                                                    onClick={() => {
                                                        redirectToTemplatesDetails();
                                                    }}
                                                    style={{ width: "257px", fontSize: "12px" }}
                                                >
                                                    View all templates
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Outlet />
                </Box>
                <RangePickerModal
                    open={openDatepicker}
                    handleClose={() => setOpenDatepicker(false)}
                    toggleDatepicker={toggleDatepicker}
                    isRepo={false}
                    isTeams={false}
                />
            </SprintsContextProvider>
        </ActiveIntegrationsProvider>
    );
};

export default Dashboard;
